import type { TokenCredential } from "@azure/identity";
import { authedJson } from "./http.js";
import { LocalCliError } from "./errors.js";

const ARM = "https://management.azure.com";
const ARM_SCOPE = "https://management.azure.com/.default";
const STORAGE_API = "2023-05-01";
const LA_API = "2023-09-01";

interface StorageList {
  value?: {
    name?: string;
    properties?: { primaryEndpoints?: { table?: string }; tags?: Record<string, string> };
    tags?: Record<string, string>;
  }[];
}
interface WorkspaceList { value?: { name?: string; properties?: { customerId?: string } }[] }

export interface LedgerResources {
  ledgerTableEndpoint: string; // https://<account>.table.core.windows.net
  workspaceId: string; // LA customerId (the data-plane query id)
}

/**
 * Discover the AgentLedger storage account's table endpoint + the Log
 * Analytics workspace customerId in the given resource group. F2 is the first
 * ledger *consumer*; both are tagged by the deploy (`m8t-stack:role`), so we
 * pick the role-tagged resources, else the lone candidate.
 */
export async function discoverLedgerResources(opts: {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
}): Promise<LedgerResources> {
  const { credential, subscriptionId, resourceGroup } = opts;

  const storage =
    (await authedJson<StorageList>({
      credential,
      scope: ARM_SCOPE,
      method: "GET",
      url: `${ARM}/subscriptions/${subscriptionId}/resourceGroups/${resourceGroup}/providers/Microsoft.Storage/storageAccounts?api-version=${STORAGE_API}`,
    })) ?? {};
  const accounts = (storage.value ?? []).filter((a) => a.properties?.primaryEndpoints?.table);
  const ledgerAcct =
    accounts.find((a) => (a.tags?.["m8t-stack:role"] ?? a.properties?.tags?.["m8t-stack:role"]) === "ledger") ??
    (accounts.length === 1 ? accounts[0] : undefined);
  const ledgerTableEndpoint = ledgerAcct?.properties?.primaryEndpoints?.table?.replace(/\/$/, "");
  if (!ledgerTableEndpoint) {
    throw new LocalCliError({
      code: "DREAM_LEDGER_STORAGE_NOT_FOUND",
      message: `No AgentLedger storage account found in resource group ${resourceGroup}.`,
      hint: `Found: ${accounts.map((a) => a.name).join(", ") || "(none)"}. Tag the ledger account 'm8t-stack:role=ledger'.`,
    });
  }

  const laList =
    (await authedJson<WorkspaceList>({
      credential,
      scope: ARM_SCOPE,
      method: "GET",
      url: `${ARM}/subscriptions/${subscriptionId}/resourceGroups/${resourceGroup}/providers/Microsoft.OperationalInsights/workspaces?api-version=${LA_API}`,
    })) ?? {};
  const ws = laList.value ?? [];
  const workspaceId = (ws.length === 1 ? ws[0] : ws.find((w) => w.properties?.customerId))?.properties?.customerId;
  if (!workspaceId) {
    throw new LocalCliError({
      code: "DREAM_LA_WORKSPACE_NOT_FOUND",
      message: `No Log Analytics workspace found in resource group ${resourceGroup}.`,
      hint: `Found: ${ws.map((w) => w.name).join(", ") || "(none)"}.`,
    });
  }

  return { ledgerTableEndpoint, workspaceId };
}
